import { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Edit,
  Trash2,
  ExternalLink,
  Calendar,
  User,
  Tag,
  Monitor,
  LayoutGrid,
  Building2,
  Users,
  Globe,
  MessageSquare,
  Network,
  AlertTriangle,
} from 'lucide-react';
import { useSpaceByKey } from '@/hooks/queries/useSpaces';
import { useObject, useObjectRelations } from '@/hooks/queries/useObjects';
import { objectsApi } from '@/lib/api';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/Card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/Avatar';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { ObjectFormModal } from '@/components/objects/ObjectFormModal';
import { RelationManager, type Relation } from '@/components/objects/RelationManager';
import { ConfirmDialog } from '@/components/ui/Dialog';
import type { ObjectType, ObjectStatus, ObjectDetailResponse, ObjectResponse } from '@arkitekt/shared';

const objectTypeIcons: Record<ObjectType, React.ReactNode> = {
  Application: <Monitor className="h-5 w-5" />,
  Technology: <LayoutGrid className="h-5 w-5" />,
  BusinessCapability: <Building2 className="h-5 w-5" />,
  Person: <Users className="h-5 w-5" />,
  Organization: <Globe className="h-5 w-5" />,
};

const objectTypeLabels: Record<ObjectType, string> = {
  Application: 'Applikasjon',
  Technology: 'Teknologi',
  BusinessCapability: 'Forretningskapabilitet',
  Person: 'Person',
  Organization: 'Organisasjon',
};

const statusVariants: Record<ObjectStatus, 'default' | 'success' | 'warning' | 'secondary'> = {
  draft: 'secondary',
  active: 'success',
  deprecated: 'warning',
  retired: 'secondary',
};

const statusLabels: Record<ObjectStatus, string> = {
  draft: 'Utkast',
  active: 'Aktiv',
  deprecated: 'Utfaset',
  retired: 'Pensjonert',
};

export function ObjectDetailPage() {
  const params = useParams<{ spaceKey: string; objectId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: space } = useSpaceByKey(params.spaceKey);
  const { data: object, isLoading } = useObject(space?.id, params.objectId);
  const { data: relationsData } = useObjectRelations(space?.id, params.objectId, 'both');

  // Modal/dialog state
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: () => objectsApi.delete(space!.id, params.objectId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['objects', space?.id] });
      navigate(`/spaces/${params.spaceKey}`);
    },
  });

  // Process relations into incoming/outgoing
  const relations = relationsData?.data ?? [];
  const incomingRelations = relations.filter((r) => r.targetId === params.objectId);
  const outgoingRelations = relations.filter((r) => r.sourceId === params.objectId);

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!object) {
    return (
      <div className="rounded-lg border border-destructive/20 bg-destructive/10 p-4 text-destructive">
        <p>Objektet ble ikke funnet</p>
      </div>
    );
  }

  return (
    <div className="max-w-5xl">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between mb-8">
        <div className="flex items-start gap-4">
          <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-muted">
            {objectTypeIcons[object.type]}
          </div>
          <div>
            <h1 className="text-2xl font-bold">{object.name}</h1>
            <div className="flex items-center gap-2 mt-2">
              <Badge variant="outline">{objectTypeLabels[object.type]}</Badge>
              <Badge variant={statusVariants[object.status]}>{statusLabels[object.status]}</Badge>
              {object.lifecyclePhase && (
                <Badge variant="secondary">{object.lifecyclePhase}</Badge>
              )}
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <Link to={`/spaces/${params.spaceKey}/graph?startId=${object.id}&depth=2`}>
            <Button variant="outline">
              <Network className="mr-2 h-4 w-4" />
              Vis i graf
            </Button>
          </Link>
          <Link to={`/impact?objectId=${object.id}&spaceKey=${params.spaceKey}`}>
            <Button variant="outline">
              <AlertTriangle className="mr-2 h-4 w-4" />
              Impact-analyse
            </Button>
          </Link>
          <Button variant="outline" onClick={() => setShowEditModal(true)}>
            <Edit className="mr-2 h-4 w-4" />
            Rediger
          </Button>
          <Button variant="destructive" onClick={() => setShowDeleteDialog(true)}>
            <Trash2 className="mr-2 h-4 w-4" />
            Slett
          </Button>
        </div>
      </div>

      {/* Edit Modal */}
      {space && (
        <ObjectFormModal
          open={showEditModal}
          onOpenChange={setShowEditModal}
          spaceId={space.id}
          object={object as ObjectResponse}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={showDeleteDialog}
        onOpenChange={setShowDeleteDialog}
        title="Slett objekt"
        description={`Er du sikker på at du vil slette "${object.name}"? Denne handlingen kan ikke angres.`}
        confirmLabel="Slett"
        cancelLabel="Avbryt"
        variant="destructive"
        onConfirm={() => deleteMutation.mutate()}
        isLoading={deleteMutation.isPending}
      />

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Main content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Description */}
          <Card>
            <CardHeader>
              <CardTitle>Beskrivelse</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground whitespace-pre-wrap">
                {object.description || 'Ingen beskrivelse er lagt til ennå.'}
              </p>
            </CardContent>
          </Card>

          {/* Type-specific attributes */}
          <ObjectAttributes object={object} />

          {/* Relations */}
          <Card>
            <CardHeader>
              <CardTitle>Relasjoner</CardTitle>
              <CardDescription>
                {incomingRelations.length + outgoingRelations.length} koblinger til andre objekter
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Outgoing Relations */}
              {space && (
                <RelationManager
                  objectId={params.objectId!}
                  objectName={object.name}
                  spaceId={space.id}
                  relations={outgoingRelations as Relation[]}
                  direction="outgoing"
                  onRelationsChange={() => queryClient.invalidateQueries({ queryKey: ['objectRelations'] })}
                />
              )}
              
              {/* Incoming Relations */}
              {space && (
                <RelationManager
                  objectId={params.objectId!}
                  objectName={object.name}
                  spaceId={space.id}
                  relations={incomingRelations as Relation[]}
                  direction="incoming"
                  onRelationsChange={() => queryClient.invalidateQueries({ queryKey: ['objectRelations'] })}
                />
              )}
            </CardContent>
          </Card>

          {/* Comments */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MessageSquare className="h-5 w-5" />
                Kommentarer
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground text-sm">
                {object.commentCount} kommentar{object.commentCount !== 1 && 'er'}
              </p>
              {/* Comments would be loaded and displayed here */}
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Metadata */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Detaljer</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Owner */}
              {object.owner && (
                <div>
                  <h4 className="text-sm text-muted-foreground mb-1">Eier</h4>
                  <div className="flex items-center gap-2">
                    <Avatar className="h-6 w-6">
                      <AvatarImage src={object.owner.avatarUrl ?? undefined} />
                      <AvatarFallback>{object.owner.displayName[0]}</AvatarFallback>
                    </Avatar>
                    <span className="text-sm font-medium">{object.owner.displayName}</span>
                  </div>
                </div>
              )}

              {/* Created */}
              <div>
                <h4 className="text-sm text-muted-foreground mb-1">Opprettet</h4>
                <div className="flex items-center gap-2 text-sm">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  {new Date(object.createdAt).toLocaleDateString('nb-NO', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric',
                  })}
                </div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground mt-1">
                  <User className="h-4 w-4" />
                  {object.createdBy.displayName}
                </div>
              </div>

              {/* Updated */}
              <div>
                <h4 className="text-sm text-muted-foreground mb-1">Sist oppdatert</h4>
                <div className="flex items-center gap-2 text-sm">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  {new Date(object.updatedAt).toLocaleDateString('nb-NO', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric',
                  })}
                </div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground mt-1">
                  <User className="h-4 w-4" />
                  {object.updatedBy.displayName}
                </div>
              </div>

              {/* Versions */}
              <div>
                <h4 className="text-sm text-muted-foreground mb-1">Versjoner</h4>
                <p className="text-sm">{object.versionCount} versjon{object.versionCount !== 1 && 'er'}</p>
              </div>
            </CardContent>
          </Card>

          {/* Tags */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Tag className="h-4 w-4" />
                Tags
              </CardTitle>
            </CardHeader>
            <CardContent>
              {object.tags.length === 0 ? (
                <p className="text-sm text-muted-foreground">Ingen tags</p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {object.tags.map((tag) => (
                    <Badge key={tag.id} variant="secondary">
                      {tag.name}
                    </Badge>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

function ObjectAttributes({ object }: { object: ObjectDetailResponse }) {
  const attrs = object.attributes;

  if (attrs.type === 'Application') {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Applikasjonsdetaljer</CardTitle>
        </CardHeader>
        <CardContent>
          <dl className="grid gap-4 sm:grid-cols-2">
            {attrs.vendor && (
              <div>
                <dt className="text-sm text-muted-foreground">Leverandør</dt>
                <dd className="font-medium">{attrs.vendor}</dd>
              </div>
            )}
            {attrs.version && (
              <div>
                <dt className="text-sm text-muted-foreground">Versjon</dt>
                <dd className="font-medium">{attrs.version}</dd>
              </div>
            )}
            <div>
              <dt className="text-sm text-muted-foreground">Kritikalitet</dt>
              <dd>
                <Badge
                  variant={
                    attrs.criticality === 'critical'
                      ? 'destructive'
                      : attrs.criticality === 'high'
                        ? 'warning'
                        : 'secondary'
                  }
                >
                  {attrs.criticality}
                </Badge>
              </dd>
            </div>
            <div>
              <dt className="text-sm text-muted-foreground">Dataklassifisering</dt>
              <dd>
                <Badge variant="outline">{attrs.dataClassification}</Badge>
              </dd>
            </div>
            <div>
              <dt className="text-sm text-muted-foreground">Hosting</dt>
              <dd className="font-medium">{attrs.hostingModel}</dd>
            </div>
            {attrs.annualCost && (
              <div>
                <dt className="text-sm text-muted-foreground">Årlig kostnad</dt>
                <dd className="font-medium">
                  {new Intl.NumberFormat('nb-NO', { style: 'currency', currency: 'NOK' }).format(attrs.annualCost)}
                </dd>
              </div>
            )}
            {attrs.productionUrl && (
              <div className="sm:col-span-2">
                <dt className="text-sm text-muted-foreground">Produksjons-URL</dt>
                <dd>
                  <a
                    href={attrs.productionUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline inline-flex items-center gap-1"
                  >
                    {attrs.productionUrl}
                    <ExternalLink className="h-3 w-3" />
                  </a>
                </dd>
              </div>
            )}
          </dl>
        </CardContent>
      </Card>
    );
  }

  if (attrs.type === 'Technology') {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Teknologidetaljer</CardTitle>
        </CardHeader>
        <CardContent>
          <dl className="grid gap-4 sm:grid-cols-2">
            <div>
              <dt className="text-sm text-muted-foreground">Kategori</dt>
              <dd className="font-medium">{attrs.category}</dd>
            </div>
            {attrs.vendor && (
              <div>
                <dt className="text-sm text-muted-foreground">Leverandør</dt>
                <dd className="font-medium">{attrs.vendor}</dd>
              </div>
            )}
            {attrs.currentVersion && (
              <div>
                <dt className="text-sm text-muted-foreground">Nåværende versjon</dt>
                <dd className="font-medium">{attrs.currentVersion}</dd>
              </div>
            )}
            <div>
              <dt className="text-sm text-muted-foreground">Støttenivå</dt>
              <dd>
                <Badge
                  variant={
                    attrs.supportLevel === 'strategic'
                      ? 'success'
                      : attrs.supportLevel === 'prohibited'
                        ? 'destructive'
                        : 'secondary'
                  }
                >
                  {attrs.supportLevel}
                </Badge>
              </dd>
            </div>
            <div>
              <dt className="text-sm text-muted-foreground">Standard</dt>
              <dd className="font-medium">{attrs.isStandard ? 'Ja' : 'Nei'}</dd>
            </div>
          </dl>
        </CardContent>
      </Card>
    );
  }

  if (attrs.type === 'BusinessCapability') {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Kapabilitetsdetaljer</CardTitle>
        </CardHeader>
        <CardContent>
          <dl className="grid gap-4 sm:grid-cols-2">
            <div>
              <dt className="text-sm text-muted-foreground">Nivå</dt>
              <dd className="font-medium">L{attrs.level}</dd>
            </div>
            <div>
              <dt className="text-sm text-muted-foreground">Strategisk viktighet</dt>
              <dd>
                <Badge variant={attrs.strategicImportance === 'core' ? 'default' : 'secondary'}>
                  {attrs.strategicImportance}
                </Badge>
              </dd>
            </div>
            <div>
              <dt className="text-sm text-muted-foreground">Investeringsprofil</dt>
              <dd>
                <Badge
                  variant={
                    attrs.investmentProfile === 'invest'
                      ? 'success'
                      : attrs.investmentProfile === 'divest'
                        ? 'destructive'
                        : 'secondary'
                  }
                >
                  {attrs.investmentProfile}
                </Badge>
              </dd>
            </div>
            {attrs.maturityScore && (
              <div>
                <dt className="text-sm text-muted-foreground">Modenhetsscore</dt>
                <dd className="font-medium">{attrs.maturityScore}/5</dd>
              </div>
            )}
          </dl>
        </CardContent>
      </Card>
    );
  }

  // Person or Organization - simpler display
  return null;
}
